import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './Modal.css';
import { ProductTypeService } from '../../services/productType.service';
import ConsumeType from '../../models/ConsumeType';
import { AuthService } from '../../services/auth.service';
import { Toast } from 'primereact/toast';
import { useShoppingCart } from '../../context/ShoppingCartContext';
import { ProductService } from '../../services/product.service';
import { StockCheckResult } from '../../models/StockCheck';
import { InsufficientItem } from '../InsufficientItem/InsufficientItem';

type ModalProps = {
  children: React.ReactNode;
  onClose: () => void;
  onAccept: (journalId: string) => void;
  onCancel: () => void;
  items: React.ReactNode;
};

export const Modal: React.FC<ModalProps> = ({
  onAccept,
  onCancel,
  children,
  items,
  onClose,
}) => {
  const { t } = useTranslation();
  const [consumeTypes, setConsumeTypes] = useState<ConsumeType[]>([]);
  const [isCentral, setIsCentral] = useState(false);
  const [selectedConsumeType, setSelectedConsumeType] = useState('');
  const [stockResults, setStockResults] = useState<StockCheckResult[] | null>(
    null,
  );
  const toast = useRef<Toast>(null);

  const { cartItems } = useShoppingCart();

  const hasInsufficient = useMemo(
    () => !!stockResults?.some((r) => r.isSufficient === false),
    [stockResults],
  );

  const insufficientItems = useMemo(
    () => stockResults?.filter((r) => r.isSufficient === false) ?? [],
    [stockResults],
  );

  useEffect(() => {
    ProductTypeService.GetConsumeType().then((response) => {
      const consumeTypes = response.data as ConsumeType[];
      if (consumeTypes != null) {
        setConsumeTypes(consumeTypes);
      }
    });
    const role = AuthService.GetRole();
    if (role === 'Central') {
      setIsCentral(true);
    }
  }, []);
  const handleAccept = () => {
    if (isCentral) {
      if (selectedConsumeType === '') {
        toast.current?.show({
          severity: 'warn',
          summary: t('sidebar_component.confirm_dialog.toast.summary'),
          detail: t('sidebar_component.confirm_dialog.toast.detail'),
          life: 3000,
        });
      } else {
        onAccept(selectedConsumeType);
      }
    } else {
      onAccept('');
    }
  };

  useEffect(() => {
    const itemsToCheck = cartItems.map((item) => ({
      ProductCode: item.idProduct,
      Quantity: item.quantity,
      ItemName: item.displayName,
    }));
    ProductService.StockCheck(itemsToCheck)
      .then((response) => {
        setStockResults(response.data);
      })
      .catch((error) => {
        console.error('StockCheck error', error);
      });
  }, [cartItems]);

  return (
    <>
      <Toast ref={toast} />
      <div className="modal-container">
        <div className="modal">
          <div className="modal-header">
            <h4 style={{ margin: 0 }}>
              {t('sidebar_component.confirm_dialog.title')}
            </h4>
            <p
              style={{ margin: 0 }}
              className="close"
              onClick={() => onClose()}
            >
              &times;
            </p>
          </div>
          <div>
            <div>
              {insufficientItems.length > 0 && (
                <>
                  <h3 style={{ color: 'tomato' }}>
                    {t('sidebar_component.confirm_dialog.insufficient_stock')}
                  </h3>
                  <ul className="insufficient-list">
                    {insufficientItems.map((item) => (
                      <InsufficientItem
                        key={item.productCode}
                        productCode={item.productCode}
                        itemName={item.itemName}
                      />
                    ))}
                  </ul>
                </>
              )}
            </div>
          </div>
          {insufficientItems.length > 0 ? (
            <h4>
              {t('sidebar_component.confirm_dialog.insufficient_stock_message')}
            </h4>
          ) : (
            children
          )}
          <hr></hr>
          {items}
          {/* Dropdown Section */}
          {isCentral ? (
            <div className="dropdown-section">
              <label htmlFor="dropdown" className="form-label">
                {t('sidebar_component.confirm_dialog.dropdown')}
              </label>
              <select
                className="form-select"
                id="dropdown"
                onChange={(e) => setSelectedConsumeType(e.target.value)}
              >
                {/* Placeholder default */}
                <option value="" disabled selected>
                  {t('sidebar_component.confirm_dialog.consume_placeholder')}
                </option>
                {consumeTypes.map((option, index) => (
                  <option key={index} value={option.name} defaultValue={''}>
                    {option.description}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          <div className="modal-footer">
            <button
              className="meso-button-accept"
              disabled={hasInsufficient}
              onClick={() => handleAccept()}
            >
              {t('sidebar_component.confirm_dialog.accept_btn')}
            </button>
            <button className="meso-button-reject" onClick={() => onCancel()}>
              {t('sidebar_component.confirm_dialog.reject_btn')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
